import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BalanceIcon from '@mui/icons-material/Balance';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

const CalculatedDeltaBalanceDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // Payment mismatches
  const [details, setDetails] = useState([]);
  const [summary, setSummary] = useState(null);
  // Open checks
  const [openChecks, setOpenChecks] = useState([]);
  const [openChecksSummary, setOpenChecksSummary] = useState(null);
  // UI state
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('balance');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'OVERPAID':
        return 'error';
      case 'UNDERPAID':
        return 'warning';
      default:
        return 'success';
    }
  };

  // Get balance color
  const getBalanceColor = (balance) => {
    const num = parseFloat(balance) || 0;
    if (num < -0.01) return 'error.main'; // Overpaid (negative)
    if (num > 0.01) return 'warning.dark'; // Underpaid (positive)
    return 'success.main'; // Balanced
  };

  // Filter data based on column filters
  const filteredDetails = useMemo(() => {
    if (!details.length) return details;

    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;

    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting
      const numericColumns = ['balance', 'expectedPayment', 'actualPayment', 'grossSales', 'totalDiscounts', 'tax', 'serviceCharges', 'tip', 'orderNumber'];
      if (numericColumns.includes(orderBy)) {
        aVal = Math.abs(parseFloat(aVal) || 0);
        bVal = Math.abs(parseFloat(bVal) || 0);
        // For balance, sort by absolute value
        if (orderBy === 'balance') {
          aVal = Math.abs(parseFloat(a[orderBy]) || 0);
          bVal = Math.abs(parseFloat(b[orderBy]) || 0);
        }
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId) {
      fetchDetails();
    }
  }, [open, businessDate, storeId]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);
      setExpandedRows({});

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      // Fetch both payment mismatches and open checks in parallel
      const [balanceResponse, openChecksResponse] = await Promise.all([
        axios.get('/hd-api/gl-export/calculated-delta-balance-details', {
          params: { businessDate, storeId },
          headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
        }),
        axios.get('/hd-api/gl-export/open-checks-details', {
          params: { businessDate, storeId },
          headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
        }),
      ]);

      setDetails(balanceResponse.data.data || []);
      setSummary(balanceResponse.data.summary || null);
      setOpenChecks(openChecksResponse.data.data || []);
      setOpenChecksSummary(openChecksResponse.data.summary || null);
    } catch (err) {
      console.error('Error fetching calculated delta balance details:', err);
      setError('Failed to load balance details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    const formatted = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Order #': item.orderNumber,
      'Expected': parseFloat(item.expectedPayment) || 0,
      'Actual': parseFloat(item.actualPayment) || 0,
      'Balance': parseFloat(item.balance) || 0,
      'Status': item.balanceStatus,
      'Gross Sales': parseFloat(item.grossSales) || 0,
      'Total Discounts': parseFloat(item.totalDiscounts) || 0,
      'Tax': parseFloat(item.tax) || 0,
      'Service Charges': parseFloat(item.serviceCharges) || 0,
      'Tip': parseFloat(item.tip) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Mismatches');
    XLSX.writeFile(wb, `balance-mismatches-${storeId}-${businessDate}.xlsx`);
  };

  // Expandable row content for Open Checks
  const OpenCheckExpandedRowContent = ({ check }) => (
    <Box sx={{ py: 2, px: 3, backgroundColor: '#fff8e1' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Open Check Breakdown
      </Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Net Sales:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(check.netSales)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">
              <AddIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Tax:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(check.tax)}</Typography>
          </Box>
          {parseFloat(check.serviceCharges) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                <AddIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                Service Charges:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(check.serviceCharges)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Total Due:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
              {formatCurrency((parseFloat(check.netSales) || 0) + (parseFloat(check.tax) || 0) + (parseFloat(check.serviceCharges) || 0))}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'success.main' }}>
            <Typography variant="body2">
              <RemoveIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Payment Collected:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(check.paymentCollected)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff3e0', p: 1, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Unpaid Amount:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'warning.dark' }}>
              {formatCurrency(check.unpaidAmount)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Open Check:</strong> This check has a status of "{check.checkStatus}" and has not been finalized.
            The {formatCurrency(check.unpaidAmount)} balance is creating an imbalance in the GL export.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
            <strong>Common causes:</strong> Forgotten to close, walk-out/dine-and-dash, POS issue, or intentionally held open.
          </Typography>
          <Box sx={{ backgroundColor: '#e3f2fd', p: 1.5, borderRadius: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
              Action Required
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Close this check in Toast to resolve the balance issue. If the guest didn't pay, apply a comp or mark as walk-out.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Expandable row content for Payment Mismatches
  const ExpandedRowContent = ({ detail }) => (
    <Box sx={{ py: 2, px: 3, backgroundColor: '#fafafa' }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Order Breakdown
      </Typography>
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">Gross Sales:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.grossSales)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'warning.dark' }}>
            <Typography variant="body2">
              <RemoveIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Item Discounts:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.itemDiscounts)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'warning.dark' }}>
            <Typography variant="body2">
              <RemoveIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Check Discounts:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.checkDiscounts)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'text.secondary' }}>
            <Typography variant="body2">
              <AddIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Tax:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.tax)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">
              <AddIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
              Service Charges:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.serviceCharges)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Expected Payment:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{formatCurrency(detail.expectedPayment)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Actual Payment:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{formatCurrency(detail.actualPayment)}</Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Balance:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontFamily: 'monospace', color: getBalanceColor(detail.balance) }}
              >
                {formatCurrency(detail.balance)}
              </Typography>
              <Chip
                label={detail.balanceStatus}
                size="small"
                color={getStatusColor(detail.balanceStatus)}
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>
          </Box>
          {parseFloat(detail.tip) > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">Tip (for reference):</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{formatCurrency(detail.tip)}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ flex: 1, pl: 2, borderLeft: '1px solid #e0e0e0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {detail.balanceStatus === 'OVERPAID' && (
              <>
                <strong>Overpaid:</strong> The guest paid more than the expected amount.
                This may require a refund of {formatCurrency(Math.abs(detail.balance))}.
              </>
            )}
            {detail.balanceStatus === 'UNDERPAID' && (
              <>
                <strong>Underpaid:</strong> The guest paid less than the expected amount.
                There may be {formatCurrency(detail.balance)} still owed.
              </>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Common causes: Discount applied after payment, partial payments, refund issues, or data anomalies.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '85vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BalanceIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Payment Balance Issues
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {memoDetail} - Store {storeId} - {businessDate}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && openChecks.length === 0 && (
          <Alert severity="info">
            No balance issues found for this entry.
          </Alert>
        )}

        {!loading && !error && (details.length > 0 || openChecks.length > 0) && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Potential Issues Detected</strong> - Review the tabs below for payment mismatches and open checks.
            </Alert>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BalanceIcon sx={{ fontSize: 18 }} />
                      Payment Mismatches
                      {details.length > 0 && (
                        <Chip label={details.length} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PendingActionsIcon sx={{ fontSize: 18 }} />
                      Open Checks
                      {openChecks.length > 0 && (
                        <Chip label={openChecks.length} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Payment Mismatches Tab */}
            {activeTab === 0 && (
              <>
                {details.length === 0 ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    No payment balance mismatches found.
                  </Alert>
                ) : (
                  <>
                    {/* Summary Stats */}
                    {summary && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Chip
                          label={`${summary.overpaidCount} Overpaid (${formatCurrency(summary.totalOverpaidAmount)})`}
                          color="error"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={`${summary.underpaidCount} Underpaid (${formatCurrency(summary.totalUnderpaidAmount)})`}
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={`Net: ${formatCurrency(summary.netBalance)}`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    )}

                    <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, backgroundColor: '#f5f5f5', pb: 1 }} />
                            {showOrderGuid && (
                              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>Order GUID</TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>Order #</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">Expected</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">Actual</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">Balance</TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedDetails.map((detail, index) => {
                            const rowKey = `${detail.orderGuid}-${detail.checkGuid}`;
                            const isExpanded = expandedRows[rowKey];

                            return (
                              <React.Fragment key={rowKey}>
                                <TableRow
                                  sx={{
                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => toggleRowExpanded(rowKey)}
                                >
                                  <TableCell sx={{ p: 0, textAlign: 'center' }}>
                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                      {isExpanded ? (
                                        <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                      ) : (
                                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  {showOrderGuid && (
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {detail.orderGuid || '-'}
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <ReceiptIcon sx={{ fontSize: 16, color: 'action.active' }} />
                                      {detail.orderNumber || '-'}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {formatCurrency(detail.expectedPayment)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {formatCurrency(detail.actualPayment)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'monospace', color: getBalanceColor(detail.balance) }}>
                                    {formatCurrency(detail.balance)}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={detail.balanceStatus}
                                      size="small"
                                      color={getStatusColor(detail.balanceStatus)}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ p: 0, border: 0 }} colSpan={showOrderGuid ? 7 : 6}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <ExpandedRowContent detail={detail} />
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Summary */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {filteredDetails.length} order{filteredDetails.length !== 1 ? 's' : ''} with balance issues
                          {hasActiveFilters && ` (filtered from ${details.length})`}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={showOrderGuid}
                              onChange={(e) => setShowOrderGuid(e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Show Order GUID</Typography>}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Click a row to see the full breakdown
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}

            {/* Open Checks Tab */}
            {activeTab === 1 && (
              <>
                {openChecks.length === 0 ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    No open checks found.
                  </Alert>
                ) : (
                  <>
                    {/* Summary Stats */}
                    {openChecksSummary && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Chip
                          icon={<PendingActionsIcon sx={{ fontSize: 16 }} />}
                          label={`${openChecksSummary.totalOpenChecks} Open Check${openChecksSummary.totalOpenChecks !== 1 ? 's' : ''}`}
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={`Total Unpaid: ${formatCurrency(openChecksSummary.totalUnpaidAmount)}`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    )}

                    <TableContainer sx={{ maxHeight: 450, userSelect: 'text' }} onCopy={handleCopy}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, backgroundColor: '#f5f5f5', pb: 1 }} />
                            {showOrderGuid && (
                              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>
                                Order GUID
                              </TableCell>
                            )}
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>
                              Order #
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">
                              Net Sales
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">
                              Collected
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', pb: 1 }} align="right">
                              Unpaid
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {openChecks.map((check, index) => {
                            const rowKey = `open-${check.orderGuid}-${check.checkGuid}`;
                            const isExpanded = expandedRows[rowKey];

                            return (
                              <React.Fragment key={rowKey}>
                                <TableRow
                                  sx={{
                                    '&:hover': { backgroundColor: '#fff8e1' },
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fffde7',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => toggleRowExpanded(rowKey)}
                                >
                                  <TableCell sx={{ p: 0, textAlign: 'center' }}>
                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                      {isExpanded ? (
                                        <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                                      ) : (
                                        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  {showOrderGuid && (
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {check.orderGuid || '-'}
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <ReceiptIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                      {check.orderNumber || '-'}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={check.checkStatus}
                                      size="small"
                                      color="warning"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                                    {formatCurrency(check.netSales)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'success.main' }}>
                                    {formatCurrency(check.paymentCollected)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, fontFamily: 'monospace', color: 'warning.dark' }}>
                                    {formatCurrency(check.unpaidAmount)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ p: 0, border: 0 }} colSpan={showOrderGuid ? 7 : 6}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <OpenCheckExpandedRowContent check={check} />
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Summary */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {openChecks.length} open check{openChecks.length !== 1 ? 's' : ''} need attention
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={showOrderGuid}
                              onChange={(e) => setShowOrderGuid(e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Show Order GUID</Typography>}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Click a row to see details
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default CalculatedDeltaBalanceDialog;
